import { Images } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { ErrorState, PageFrame } from "@/components/common";
import { EmptyState } from "@/components/ui";
import { getApiErrorMessage } from "@/lib/errorMessages";
import { regionDisplayName } from "@/lib/regionLabel";
import { Permission } from "@/types";
import { useAuthStore } from "@/store/authStore";
import CarouselSlideForm from "./CarouselSlideForm";
import { CAROUSEL_LIST_PATH } from "./carouselSlideFormModel";
import { useCarouselRegion } from "./useCarouselRegion";

export default function CarouselSlideCreatePage() {
  const { t } = useTranslation("adminCarousel");
  const { t: tCommon } = useTranslation("common");
  const { t: tErrors } = useTranslation("errors");
  const [searchParams] = useSearchParams();
  const { regions, isLoading, error, retry, isRetrying, regionId } =
    useCarouselRegion(searchParams.get("region_id") ?? "");
  const canWrite = useAuthStore((s) =>
    s.hasPermission(Permission.CAROUSEL_SLIDES_WRITE),
  );
  const region = regions.find((row) => row.id === regionId);

  return (
    <PageFrame
      title={t("form.createTitle")}
      description={
        region
          ? t("form.createDescription", {
              region: regionDisplayName(region.code, tCommon, region.name),
            })
          : undefined
      }
      icon={Images}
      isLoading={isLoading}
      backLink={{ fallback: CAROUSEL_LIST_PATH, children: t("title") }}
    >
      {error ? (
        <ErrorState
          title={tErrors("loadRegions")}
          message={getApiErrorMessage(error)}
          onRetry={retry}
          isRetrying={isRetrying}
        />
      ) : regionId ? (
        <CarouselSlideForm
          key={regionId}
          initial={null}
          regionId={regionId}
          canWrite={canWrite}
        />
      ) : (
        <EmptyState
          variant="dashed"
          icon={Images}
          title={tCommon("region.empty")}
        />
      )}
    </PageFrame>
  );
}
